import {
  buildLegacyUpdatePayload,
  UPDATE_ERROR_FIELDS,
  type AddItemFields,
  type AddItemStep,
} from "@kovernow/flows/add_item";
import { acceptFor, type UploadQueue } from "@kovernow/flows/uploads";
import { documentsOf, mergeDocuments, ownStorageLocation, type VaultItem } from "@kovernow/flows/vault";
import { Button } from "@kovernow/react-ui/components/button";
import { UploadField } from "@kovernow/react-ui/components/upload-field";
import { useState, type RefObject } from "react";
import { GatedButton } from "../../components/GatedButton";
import { useServices } from "../../core/app-context";
import { useT } from "../../core/use-t";
import { STEP_HEADING_ID, StepHeading, type ServerFieldErrors } from "../add-item/shared";
import { BusyLine, PipelineStatus, useUploadPipeline } from "../add-item/upload-pipeline";

const MAX_PHOTOS = 10;

/**
 * Legacy edit, last step: the stored item photos as removable rows, then
 * an UploadField for new ones (a fresh W-4 queue). Kept photos plus queued
 * files stay within 1–10, and the last photo can't be removed. "Save
 * changes" uploads the new photos, then sends `PUT /items` with the merged
 * documents; only after the 2xx are removed photos deleted from Storage
 * (the caller's `users/{uid}/` objects only, best effort).
 */
export function EditPhotosStep({
  item,
  fields,
  removed,
  setRemoved,
  queue,
  ownerId,
  inFlight,
  headingRef,
  serverErrors,
  clearServerError,
  back,
  onSaved,
  onFieldError,
  onApiError,
}: {
  item: VaultItem;
  fields: AddItemFields;
  /** URLs of stored photos removed in this edit (lifted: Back/Forward keeps them). */
  removed: ReadonlySet<string>;
  setRemoved: (update: (s: ReadonlySet<string>) => ReadonlySet<string>) => void;
  queue: UploadQueue;
  ownerId: string | null;
  inFlight: { current: boolean };
  headingRef: RefObject<HTMLHeadingElement | null>;
  serverErrors: ServerFieldErrors;
  clearServerError: (field: string) => void;
  back: () => void;
  onSaved: (updated: VaultItem) => void;
  onFieldError: (step: AddItemStep, field: string, messageKey: string) => void;
  onApiError: (err: unknown) => boolean;
}) {
  const t = useT();
  const { vaultApi, uploads, config } = useServices();
  const [attempted, setAttempted] = useState(false);
  const [overLimit, setOverLimit] = useState(false);
  const stored = documentsOf(item, "item");
  const kept = stored.filter((d) => !removed.has(d.url));

  const pipeline = useUploadPipeline({
    queue,
    ownerId,
    inFlight,
    errorFields: UPDATE_ERROR_FIELDS,
    onApiError,
    createItem: async () => {
      const documents = mergeDocuments(item.documents ?? [], queue.documents(), removed);
      const updated = await vaultApi.update(buildLegacyUpdatePayload(item, fields, documents, config.currency));
      // Storage only after the server confirmed; best effort, own prefix only.
      const gone = stored
        .filter((d) => removed.has(d.url))
        .map((d) => ownStorageLocation(d, ownerId))
        .filter((l): l is string => !!l);
      await Promise.allSettled(gone.map((l) => uploads.remove(ownerId, l)));
      onSaved(updated);
    },
    onFieldError: (target, messageKey) => onFieldError(target.step, target.field, messageKey),
  });
  const { qs, busy, locked, rowsFor, limitError, common } = pipeline;

  const total = kept.length + queue.count("photo");
  const minError = total < 1;
  const save = () => {
    setAttempted(true);
    if (minError || queue.preparing()) return;
    void pipeline.run(() => queue.uploadPending(ownerId));
  };

  const photoError =
    (serverErrors.photos ? t(serverErrors.photos) : undefined) ??
    (overLimit ? t("itemDetail.edit.tooManyPhotos") : undefined) ??
    limitError("photo") ??
    (attempted && minError ? t("addItem.errors.photosMin") : undefined);

  return (
    <section aria-labelledby={STEP_HEADING_ID} className="flex flex-col gap-6" data-testid="step-photos">
      <StepHeading headingRef={headingRef}>{t("itemDetail.edit.photos")}</StepHeading>

      {kept.length ? (
        <div className="flex flex-col gap-2">
          <h3 className="font-semibold">{t("itemDetail.edit.stored")}</h3>
          <ul className="flex flex-col divide-y rounded-lg border" data-testid="stored-photos">
            {kept.map((d, i) => {
              const last = total <= 1;
              return (
                <li key={d.url} className="flex items-center justify-between gap-3 px-4 py-2">
                  <img src={d.url} alt="" className="size-14 shrink-0 rounded-md object-cover" />
                  <span className="min-w-0 flex-1">{t("itemDetail.documents.file.item", { n: i + 1 })}</span>
                  <div className="flex flex-col items-end gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="touch"
                      aria-disabled={last || locked ? true : undefined}
                      aria-describedby={last ? "last-photo-reason" : undefined}
                      onClick={() => {
                        if (last || locked) return;
                        clearServerError("photos");
                        setOverLimit(false);
                        setRemoved((s) => new Set([...s, d.url]));
                      }}
                    >
                      {t("itemDetail.edit.removePhoto", { n: i + 1 })}
                    </Button>
                    {last ? (
                      <p id="last-photo-reason" className="text-sm text-muted-foreground">
                        {t("itemDetail.edit.lastPhoto")}
                      </p>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      <UploadField
        {...common}
        testId="upload-photos"
        label={t("itemDetail.edit.addPhotos")}
        limits={t("itemDetail.edit.photoLimit")}
        accept={acceptFor("photo")}
        rows={rowsFor("photo")}
        error={photoError}
        onRemove={(id) => {
          setOverLimit(false);
          queue.remove(id);
        }}
        onFiles={(files) => {
          clearServerError("photos");
          // Kept stored photos count toward the 10.
          const room = Math.max(0, MAX_PHOTOS - kept.length - queue.count("photo"));
          setOverLimit(files.length > room);
          void queue.add("photo", files.slice(0, room));
        }}
      />

      <PipelineStatus uploadsFailed={pipeline.uploadsFailed} recovery={pipeline.recovery} onRetry={save} />

      <div className="flex flex-col gap-2 sm:flex-row-reverse sm:items-start">
        <div className="sm:min-w-56">
          <GatedButton
            variant="cta"
            label={t("addItem.saveChanges")}
            busy={locked}
            unavailableReason={qs.files.some((f) => f.status === "preparing") ? t("common.loading") : undefined}
            onClick={save}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="touch"
          aria-disabled={locked ? true : undefined}
          onClick={() => {
            if (!locked) back();
          }}
        >
          {t("addItem.back")}
        </Button>
      </div>
      <BusyLine busy={busy} />
    </section>
  );
}
